import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { SectionHeader } from "@/components/common/Widgets";
import ContextualUploader from "@/components/common/ContextualUploader";
import { Plus, Trash2 } from "lucide-react";
import { fmtNum } from "@/lib/format";

const KINDS = ["PRIMARY_HYPO", "COLLATERAL_LAND", "COLLATERAL_BUILDING", "MACHINERY", "STDR", "INSURANCE", "PERSONAL_GUARANTEE"];

export default function SecuritySection({ app, security, reload }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ kind: "COLLATERAL_BUILDING", description: "", valuation_lakhs: "", valuer_name: "", valuation_date: "", location: "", insurance_company: "", insurance_expiry: "", notes: "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [busy, setBusy] = useState(false);

  const totalSec = (security || []).reduce((s, x) => s + (x.valuation_lakhs || 0), 0);
  const totalFac = (app.cc_proposed || 0) + (app.tl_proposed || 0);
  const coverPct = totalFac > 0 ? (totalSec / totalFac) * 100 : 0;

  const add = async () => {
    if (!form.description) return toast.error("Description required");
    setBusy(true);
    try {
      const payload = { ...form, application_id: app.id, valuation_lakhs: form.valuation_lakhs ? Number(form.valuation_lakhs) : null };
      await api.post("/security", payload);
      toast.success("Security added");
      setForm({ kind: "COLLATERAL_BUILDING", description: "", valuation_lakhs: "", valuer_name: "", valuation_date: "", location: "", insurance_company: "", insurance_expiry: "", notes: "" });
      reload && reload();
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this security item?")) return;
    try { await api.delete(`/security/${id}`); toast.success("Removed"); reload && reload(); } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-3">
      <SectionHeader title={t("tabs.securityTitle", "Security & Collateral")} subtitle={t("tabs.securitySubtitle", "Annexure II / III equivalent. Capture all collateral, hypothecation and insurances.")} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat k="Total Facility (₹ L)" v={fmtNum(totalFac)} />
        <Stat k="Total Security (₹ L)" v={fmtNum(totalSec)} />
        <Stat k="Security Cover %" v={fmtNum(coverPct, 1) + "%"} hint={coverPct >= 100 ? "Fully covered" : (coverPct >= 75 ? "Partial cover" : "Low cover")} />
        <Stat k="Items" v={(security || []).length} />
      </div>

      <Card className="p-4">
        <div className="text-sm font-semibold heading mb-3">Add Security / Collateral</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Row label="Type">
            <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
              <SelectTrigger data-testid="security-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{KINDS.map(k => <SelectItem key={k} value={k}>{k.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </Row>
          <Row label="Description"><Input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g., Land + Building at Naharlagun" data-testid="security-description" /></Row>
          <Row label="Valuation (₹ L)"><Input type="number" value={form.valuation_lakhs} onChange={(e) => set("valuation_lakhs", e.target.value)} data-testid="security-value" /></Row>
          <Row label="Valuer"><Input value={form.valuer_name} onChange={(e) => set("valuer_name", e.target.value)} /></Row>
          <Row label="Valuation Date"><Input type="date" value={form.valuation_date} onChange={(e) => set("valuation_date", e.target.value)} /></Row>
          <Row label="Location"><Input value={form.location} onChange={(e) => set("location", e.target.value)} /></Row>
          <Row label="Insurance Co."><Input value={form.insurance_company} onChange={(e) => set("insurance_company", e.target.value)} /></Row>
          <Row label="Insurance Expiry"><Input type="date" value={form.insurance_expiry} onChange={(e) => set("insurance_expiry", e.target.value)} /></Row>
          <Row label="Notes"><Textarea rows={1} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Row>
        </div>
        <div className="mt-3 flex justify-between items-center gap-3 flex-wrap">
          <ContextualUploader applicationId={app.id} category="COLLATERAL" title="Attach valuation / title docs" multiple compact />
          <Button onClick={add} disabled={busy} data-testid="security-add-button"><Plus className="h-4 w-4 mr-1" />{t("tabs.securityAdd", "Add Item")}</Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Description</th><th className="text-right px-3 py-2">Value (₹ L)</th><th className="text-left px-3 py-2">Valuer</th><th className="text-left px-3 py-2">Location</th><th className="text-left px-3 py-2">Insurance Expiry</th><th className="text-right px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {(security || []).length === 0 && <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No security added yet.</td></tr>}
              {(security || []).map(s => (
                <tr key={s.id} className="border-t border-border">
                  <td className="px-3 py-2"><Badge variant="secondary">{s.kind}</Badge></td>
                  <td className="px-3 py-2">{s.description}</td>
                  <td className="px-3 py-2 num-right">{fmtNum(s.valuation_lakhs)}</td>
                  <td className="px-3 py-2 text-xs">{s.valuer_name || "—"}</td>
                  <td className="px-3 py-2 text-xs">{s.location || "—"}</td>
                  <td className="px-3 py-2 text-xs mono">{s.insurance_expiry || "—"}</td>
                  <td className="px-3 py-2 text-right"><Button variant="outline" size="sm" onClick={() => remove(s.id)}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
function Row({ label, children }) { return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</Label>{children}</div>); }
function Stat({ k, v, hint }) { return (<Card className="p-3 shadow-card"><div className="text-[11px] uppercase text-muted-foreground font-medium">{k}</div><div className="text-xl mono font-semibold tabular mt-0.5">{v}</div>{hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}</Card>); }
